@if (completeProfileService.config$ | async) {
  <div
    class="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center sm:p-4 backdrop-blur-sm animate-fade-in"
  >
    <div
      class="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl overflow-hidden max-h-[90vh] flex flex-col animate-scale-in"
      (click)="$event.stopPropagation()"
    >
      <div class="flex flex-col sm:flex-row flex-1 overflow-hidden">
        <!-- Left panel -->
        <div
          class="bg-gradient-to-br from-brand to-brand-dark px-6 py-6 sm:py-8 sm:px-8 sm:w-[40%] flex flex-col justify-center relative overflow-hidden shrink-0"
        >
          <div class="absolute -bottom-10 -left-10 w-40 h-40 bg-white/5 rounded-full"></div>
          <div class="absolute -top-10 -right-10 w-32 h-32 bg-white/5 rounded-full"></div>

          <div class="relative z-10">
            <h2 class="text-xl sm:text-2xl font-extrabold text-white mb-2 leading-tight">
              {{ 'common.auth.completeProfile.leftPanelHeading' | translate }}
            </h2>
            <p class="text-white/70 text-sm leading-relaxed">
              {{ 'common.auth.completeProfile.leftPanelDescription' | translate }}
            </p>
          </div>
        </div>

        <!-- Right panel -->
        <div class="flex-1 overflow-y-auto">
          <div class="px-5 py-5 sm:px-6 sm:py-6">
            <div class="flex items-center justify-center mb-4">
              <img src="images/logo.png" alt="JustTeach" class="h-6 w-auto" />
            </div>

            @if (errorMessage$ | async; as errorMsg) {
              <div
                class="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl mb-5 text-sm font-medium animate-shake"
              >
                <svg
                  class="w-5 h-5 flex-shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {{ errorMsg }}
              </div>
            }

            <form [formGroup]="profileForm" (ngSubmit)="onSubmit()" class="space-y-4">
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1.5">
                    {{ 'common.auth.signUp.firstNameLabel' | translate }}
                  </label>
                  <div class="relative">
                    <div
                      class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"
                    >
                      <lucide-icon [img]="icons.User" class="w-4 h-4"></lucide-icon>
                    </div>
                    <input
                      type="text"
                      formControlName="firstName"
                      class="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium text-sm"
                      [class.border-red-400]="
                        profileForm.get('firstName')?.invalid &&
                        profileForm.get('firstName')?.touched
                      "
                      [placeholder]="'common.auth.signUp.firstNamePlaceholder' | translate"
                      autocomplete="given-name"
                    />
                  </div>
                  @if (
                    profileForm.get('firstName')?.invalid && profileForm.get('firstName')?.touched
                  ) {
                    <p class="mt-1 text-xs text-red-500 font-medium">
                      {{ 'common.auth.signUp.firstNameValidation' | translate }}
                    </p>
                  }
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1.5">
                    {{ 'common.auth.signUp.lastNameLabel' | translate }}
                  </label>
                  <div class="relative">
                    <div
                      class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"
                    >
                      <lucide-icon [img]="icons.User" class="w-4 h-4"></lucide-icon>
                    </div>
                    <input
                      type="text"
                      formControlName="lastName"
                      class="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium text-sm"
                      [class.border-red-400]="
                        profileForm.get('lastName')?.invalid && profileForm.get('lastName')?.touched
                      "
                      [placeholder]="'common.auth.signUp.lastNamePlaceholder' | translate"
                      autocomplete="family-name"
                    />
                  </div>
                  @if (
                    profileForm.get('lastName')?.invalid && profileForm.get('lastName')?.touched
                  ) {
                    <p class="mt-1 text-xs text-red-500 font-medium">
                      {{ 'common.auth.signUp.lastNameValidation' | translate }}
                    </p>
                  }
                </div>
              </div>

              @if (profileType() === 'teacher') {
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1.5">
                    {{ 'common.auth.signUp.phoneNumberLabel' | translate }}
                  </label>
                  <app-phone-input
                    formControlName="phoneNumber"
                    [hasError]="
                      !!profileForm.get('phoneNumber')?.invalid &&
                      !!profileForm.get('phoneNumber')?.touched
                    "
                  ></app-phone-input>
                  @if (
                    profileForm.get('phoneNumber')?.hasError('required') &&
                    profileForm.get('phoneNumber')?.touched
                  ) {
                    <p class="mt-1 text-xs text-red-500 font-medium">
                      {{ 'common.auth.signUp.phoneNumberValidation' | translate }}
                    </p>
                  }
                  @if (
                    profileForm.get('phoneNumber')?.hasError('validatePhoneNumber') &&
                    profileForm.get('phoneNumber')?.touched
                  ) {
                    <p class="mt-1 text-xs text-red-500 font-medium">
                      {{ 'common.auth.signUp.phoneNumberInvalid' | translate }}
                    </p>
                  }
                </div>
              }

              @if (profileType() === 'student') {
                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1.5">
                    {{ 'common.auth.signUp.parentNameLabel' | translate }}
                  </label>
                  <div class="relative">
                    <div
                      class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400"
                    >
                      <lucide-icon [img]="icons.User" class="w-4 h-4"></lucide-icon>
                    </div>
                    <input
                      type="text"
                      formControlName="parentName"
                      class="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none transition-all font-medium text-sm"
                      [class.border-red-400]="
                        profileForm.get('parentName')?.invalid &&
                        profileForm.get('parentName')?.touched
                      "
                      [placeholder]="'common.auth.signUp.parentNamePlaceholder' | translate"
                      autocomplete="off"
                    />
                  </div>
                  @if (
                    profileForm.get('parentName')?.invalid && profileForm.get('parentName')?.touched
                  ) {
                    <p class="mt-1 text-xs text-red-500 font-medium">
                      {{ 'common.auth.signUp.parentNameValidation' | translate }}
                    </p>
                  }
                </div>

                <div>
                  <label class="block text-sm font-semibold text-gray-700 mb-1.5">
                    {{ 'common.auth.signUp.parentPhoneLabel' | translate }}
                  </label>
                  <app-phone-input
                    formControlName="parentPhoneNumber"
                    [hasError]="
                      !!profileForm.get('parentPhoneNumber')?.invalid &&
                      !!profileForm.get('parentPhoneNumber')?.touched
                    "
                  ></app-phone-input>
                  @if (
                    profileForm.get('parentPhoneNumber')?.hasError('required') &&
                    profileForm.get('parentPhoneNumber')?.touched
                  ) {
                    <p class="mt-1 text-xs text-red-500 font-medium">
                      {{ 'common.auth.signUp.parentPhoneValidation' | translate }}
                    </p>
                  }
                  @if (
                    profileForm.get('parentPhoneNumber')?.hasError('validatePhoneNumber') &&
                    profileForm.get('parentPhoneNumber')?.touched
                  ) {
                    <p class="mt-1 text-xs text-red-500 font-medium">
                      {{ 'common.auth.signUp.phoneNumberInvalid' | translate }}
                    </p>
                  }
                </div>
              }

              <button
                type="submit"
                class="w-full bg-brand hover:bg-brand-dark text-white font-bold py-3.5 rounded-xl transition-all shadow-lg shadow-brand/20 flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                [disabled]="isLoading$ | async"
              >
                @if (isLoading$ | async) {
                  <svg
                    class="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {{ 'common.auth.completeProfile.saving' | translate }}
                } @else {
                  {{ 'common.auth.completeProfile.continue' | translate }}
                  <lucide-icon
                    [img]="icons.ArrowRight"
                    class="w-5 h-5 group-hover:translate-x-1 transition-transform"
                  ></lucide-icon>
                }
              </button>
            </form>
          </div>
        </div>
      </div>

      <div class="h-safe-area-inset-bottom sm:hidden"></div>
    </div>
  </div>
}
